import React, { useState } from 'react';
import { sirenDigits, sirenFrom } from '../../../utils/siren';
import { useI18n } from '../../../contexts/I18nContext';
import { ClientWithDevices } from '../../../types/client';
import { hueOf } from '../../users/UserAvatar';
import { clientInitials } from '../clientInitials';
import { AssignDevicesModal } from '../AssignDevicesModal';
import { ClientDetailState } from './useClientDetail';

interface Props {
  state: ClientDetailState;
  onOpenClient?: (client: ClientWithDevices) => void;
  /** Whether this person may assign gateways to an agency (ANEP managers, client admins). */
  canAssignDevices?: boolean;
}

/**
 * The Overview tab: identity (name, SIREN, status, access), the group the
 * client belongs to (its root, or its agencies) and activity, edited in place.
 */
export const ClientOverviewTab: React.FC<Props> = ({ state, onOpenClient, canAssignDevices = false }) => {
  const { formatDateTime, formatNumber, t } = useI18n();
  const { client, isEditing, isSaving, editedName, editedSiren, sirenValid, root, agencies, clientIsAgency } = state;
  const [assignTarget, setAssignTarget] = useState<ClientWithDevices | null>(null);
  if (!client) return null;
  const accessOn = client.provisioning_access ?? true;
  const canAssign = canAssignDevices;
  const siret = clientIsAgency && client.nic && root?.siren ? `${root.siren}${client.nic}` : null;

  return (
    <div className="tab-content">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('clients.detail.identity')}</h3>
          <p>{t('clients.detail.identityIntro')}</p>
        </header>
        {isEditing ? (
          <div className="cu-grid">
            <div className="cu-field cu-field-wide">
              <label htmlFor="clName">{t('clients.clientName')} *</label>
              <input
                type="text"
                id="clName"
                data-testid="field-client-name-edit"
                value={editedName}
                onChange={(e) => state.setEditedName(e.target.value)}
                className={`cu-input${editedName.trim() ? '' : ' is-invalid'}`}
                disabled={isSaving}
                autoFocus
              />
            </div>
            <div className="cu-field">
              <label htmlFor="clSiren">{t('clients.sirenOrSiret')}</label>
              <input
                type="text"
                id="clSiren"
                data-testid="field-client-siren-edit"
                inputMode="numeric"
                value={editedSiren}
                onChange={(e) => state.setEditedSiren(sirenDigits(e.target.value))}
                className={`cu-input mono${!sirenValid ? ' is-invalid' : ''}`}
                placeholder={t('clients.detail.sirenPlaceholder')}
                maxLength={14}
                disabled={isSaving}
              />
              {editedSiren && !sirenValid
                ? <span className="cu-error" data-testid="error-client-siren">{t('clients.sirenDigitsHint')}</span>
                : sirenFrom(editedSiren).kind === 'siret'
                  ? <span className="cu-hint is-ok">{t('clients.siretOk', { siren: sirenFrom(editedSiren).siren ?? '' })}</span>
                  : editedSiren && sirenValid
                    ? <span className="cu-hint is-ok">{t('clients.detail.sirenOk')}</span>
                    : <span className="cu-hint">{t('clients.detail.sirenHint')}</span>}
            </div>
            <div className="cu-field">
              <label htmlFor="clStatus">{t('clients.detail.statusLabel')}</label>
              <select
                id="clStatus"
                data-testid="field-client-active-edit"
                value={state.editedActive ? 'active' : 'inactive'}
                onChange={(e) => state.setEditedActive(e.target.value === 'active')}
                className="cu-input"
                disabled={isSaving}
              >
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
              <span className="cu-hint">{t('clients.detail.statusHint')}</span>
            </div>
            <div className="cu-field">
              <label htmlFor="clAccess">{t('clients.detail.accessLabel')}</label>
              <select
                id="clAccess"
                data-testid="field-client-access-edit"
                value={state.editedAccess ? 'on' : 'off'}
                onChange={(e) => state.setEditedAccess(e.target.value === 'on')}
                className="cu-input"
                disabled={isSaving}
              >
                <option value="on">{t('clients.detail.accessOn')}</option>
                <option value="off">{t('clients.detail.accessOff')}</option>
              </select>
              <span className="cu-hint">{t('clients.detail.accessHint')}</span>
            </div>
            {clientIsAgency && (
              <div className="cu-field">
                <label htmlFor="clNic">{t('clients.agencies.nic')}</label>
                <input
                  type="text"
                  id="clNic"
                  data-testid="field-client-nic-edit"
                  inputMode="numeric"
                  value={state.editedNic}
                  onChange={(e) => state.setEditedNic(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className={`cu-input mono${state.nicValid ? '' : ' is-invalid'}`}
                  maxLength={5}
                  disabled={isSaving}
                />
                <span className="cu-hint">{t('clients.agencies.nicHint')}</span>
              </div>
            )}
          </div>
        ) : (
          <dl className="tpd-facts">
            <div className="tpd-fact">
              <dt>{t('clients.clientName')}</dt>
              <dd>{client.name}</dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('clients.sirenLabel')}</dt>
              <dd>{client.siren ? <code className="dt-code">{client.siren}</code> : <span className="tpd-muted">{t('clients.detail.none')}</span>}</dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('clients.detail.statusLabel')}</dt>
              <dd>
                <span className={`status-badge ${client.active ? 'active' : 'inactive'}`}>
                  {client.active ? t('common.active') : t('common.inactive')}
                </span>
              </dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('clients.detail.accessLabel')}</dt>
              <dd>
                <span className={`status-badge ${accessOn ? 'active' : 'inactive'}`}>
                  {accessOn ? t('clients.detail.accessOn') : t('clients.detail.accessOff')}
                </span>
              </dd>
            </div>
            {clientIsAgency && (
              <div className="tpd-fact">
                <dt>{t('clients.agencies.root')}</dt>
                <dd data-testid="client-root">
                  {root
                    ? <button type="button" className="cu-link" data-testid="btn-open-root" onClick={() => onOpenClient?.(root)}>{root.name}</button>
                    : <span className="tpd-muted">{client.parentClientId ? `#${client.parentClientId}` : t('clients.agencies.unknownRoot')}</span>}
                </dd>
              </div>
            )}
            {clientIsAgency && (
              <div className="tpd-fact">
                <dt>{siret ? t('clients.agencies.siret') : t('clients.agencies.nic')}</dt>
                <dd>{siret ? <code className="dt-code">{siret}</code> : client.nic ? <code className="dt-code">{client.nic}</code> : <span className="tpd-muted">{t('clients.detail.none')}</span>}</dd>
              </div>
            )}
            <div className="tpd-fact tpd-fact-wide">
              <dt>{t('clients.clientId')}</dt>
              <dd><code className="dt-code">{client.id}</code></dd>
            </div>
          </dl>
        )}
      </section>

      {!isEditing && (clientIsAgency || agencies.length > 0) && (
        <section className="cu-section" data-testid="client-group-section">
          <header className="cu-section-head">
            <h3>{clientIsAgency ? t('clients.agencies.attachment') : t('clients.agencies.section', { count: agencies.length })}</h3>
            <p>{clientIsAgency ? t('clients.agencies.attachmentIntro') : t('clients.agencies.sectionIntro')}</p>
            {canAssign && clientIsAgency && (
              <div className="cu-section-actions">
                <button type="button" className="btn btn-secondary btn-sm" data-testid="btn-assign-devices" onClick={() => setAssignTarget(client)}>{t('clients.agencies.assign')}</button>
              </div>
            )}
          </header>
          {clientIsAgency ? (
            <p className="cu-note">
              {root
                ? <>{t('clients.agencies.agencyOf', { name: root.name })} <button type="button" className="cu-link" onClick={() => onOpenClient?.(root)}>{t('clients.agencies.openRoot')}</button></>
                : t('clients.agencies.unknownRoot')}
            </p>
          ) : (
            <ul className="cl-agencies" data-testid="client-agencies">
              {agencies.map((agency) => (
                <li key={agency.id} className={`cl-agency${agency.active ? '' : ' is-off'}`} data-testid={`agency-${agency.id}`}>
                  <span className="cl-avatar" style={{ ['--cl-hue' as any]: hueOf(agency.id) }} aria-hidden="true">{clientInitials(agency.name)}</span>
                  <div className="cl-agency-text">
                    <strong>{agency.name}</strong>
                    <span className="tpl-sub">
                      {agency.nic ? `${t('clients.agencies.nic')} ${agency.nic} · ` : ''}{t('clients.agencies.devices', { count: formatNumber(agency.deviceCount || 0) })}
                    </span>
                  </div>
                  <div className="cl-agency-actions">
                    {canAssign && <button type="button" className="btn btn-secondary btn-sm" data-testid={`btn-assign-${agency.id}`} onClick={() => setAssignTarget(agency)}>{t('clients.agencies.assign')}</button>}
                    <button type="button" className="btn btn-secondary btn-sm" data-testid={`btn-open-agency-${agency.id}`} onClick={() => onOpenClient?.(agency)}>{t('clients.agencies.open')}</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <AssignDevicesModal target={assignTarget} isOpen={assignTarget !== null} onClose={() => setAssignTarget(null)} onDone={state.fetchDevices} />

      {!isEditing && (
        <section className="cu-section">
          <header className="cu-section-head">
            <h3>{t('clients.detail.activity')}</h3>
            <p>{t('clients.detail.activityIntro')}</p>
          </header>
          <dl className="tpd-facts">
            <div className="tpd-fact">
              <dt>{t('clients.totalDevices')}</dt>
              <dd className="cl-big">
                {formatNumber(agencies.length > 0 ? client.deviceCount + agencies.reduce((s, a) => s + (a.deviceCount || 0), 0) : client.deviceCount)}
                {agencies.length > 0 && client.deviceCount > 0 && <span className="cu-error cl-big-note">{t('clients.agencies.unassigned', { count: formatNumber(client.deviceCount) })}</span>}
              </dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('clients.lastActivity')}</dt>
              <dd>{client.lastActivity ? formatDateTime(client.lastActivity) : t('clients.detail.none')}</dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('clients.created')}</dt>
              <dd>{formatDateTime(client.created_at)}</dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('clients.updated')}</dt>
              <dd>{formatDateTime(client.updated_at)}</dd>
            </div>
          </dl>
        </section>
      )}
    </div>
  );
};
